import type { PaperExamListRow } from '../../api/types';
import { gradeLabel } from '../../lib/gradeLabel';
import { plural } from '../../lib/plural';

export type PaperStatus = 'review' | 'collect' | 'marked' | 'print';

/** Sheets the exam is waiting for: its audience size, never fewer than what is already in. */
export function expectedSheets(e: PaperExamListRow): number {
  return Math.max(e.sheets_expected ?? 0, e.sheets.total);
}

/**
 * Status from the sheet counts (the design's four): sheets waiting for review win; nothing
 * uploaded yet is "Ready to print"; every expected sheet confirmed is "Marked"; otherwise the
 * exam is still collecting. Without `sheets_expected` (older backend) "expected" is what is in.
 */
export function paperStatus(e: PaperExamListRow): PaperStatus {
  if ((e.sheets_needs_review ?? 0) > 0) return 'review';
  if (e.sheets.total === 0) return 'print';
  return e.sheets.confirmed >= expectedSheets(e) ? 'marked' : 'collect';
}

/** "Grade 10 A" → "A" (the artboard lists section letters after the grade). */
export function sectionLetters(e: PaperExamListRow): string {
  const prefixes = [e.grade?.name, e.grade ? gradeLabel(e.grade.name) : undefined]
    .filter((p): p is string => !!p)
    .sort((a, b) => b.length - a.length);
  return (e.sections ?? [])
    .map((display) => {
      const hit = prefixes.find((p) => display.toLowerCase().startsWith(p.toLowerCase()));
      const rest = hit ? display.slice(hit.length) : display;
      return rest.replace(/^[\s·\-–]+/, '').trim() || display;
    })
    .join(', ');
}

export interface ListStats {
  exams: number;
  /** Exams created in the current calendar month. */
  newThisMonth: number;
  subjects: number;
  /** "9–12" from the grade names' numbers, or the single grade; null when none parse. */
  gradeSpan: string | null;
  uploaded: number;
  confirmed: number;
  /** Total expected sheets; 0 when the backend does not send it. */
  expected: number;
  /** Total sheets waiting for review; null when no row carries the field. */
  needsReview: number | null;
  examsNeedingReview: number;
  /** Mean of the averages of fully marked exams; null when there are none. */
  avgScore: number | null;
  markedExams: number;
  /** Latest marked exam's average minus the one before it; null with fewer than two. */
  avgDelta: number | null;
}

export function listStats(exams: PaperExamListRow[], now: Date = new Date()): ListStats {
  const monthKey = `${now.getFullYear()}-${now.getMonth()}`;
  const newThisMonth = exams.filter((e) => {
    const d = new Date(e.created);
    return !Number.isNaN(d.getTime()) && `${d.getFullYear()}-${d.getMonth()}` === monthKey;
  }).length;
  const subjects = new Set(exams.map((e) => e.subject?.id).filter((id): id is number => id != null)).size;
  const gradeNums = exams
    .map((e) => Number((e.grade?.name ?? '').match(/\d+/)?.[0]))
    .filter((n) => Number.isFinite(n) && n > 0);
  const lo = gradeNums.length ? Math.min(...gradeNums) : null;
  const hi = gradeNums.length ? Math.max(...gradeNums) : null;
  const gradeSpan = lo == null || hi == null ? null : lo === hi ? String(lo) : `${lo}–${hi}`;

  const hasReviewField = exams.some((e) => e.sheets_needs_review != null);
  const marked = exams
    .filter((e) => paperStatus(e) === 'marked' && e.average != null)
    .sort((a, b) => (a.last_scan_at ?? '').localeCompare(b.last_scan_at ?? ''));
  const avgScore = marked.length ? marked.reduce((n, e) => n + (e.average as number), 0) / marked.length : null;
  const avgDelta = marked.length >= 2
    ? (marked[marked.length - 1].average as number) - (marked[marked.length - 2].average as number)
    : null;

  return {
    exams: exams.length,
    newThisMonth,
    subjects,
    gradeSpan,
    uploaded: exams.reduce((n, e) => n + e.sheets.total, 0),
    confirmed: exams.reduce((n, e) => n + e.sheets.confirmed, 0),
    expected: exams.reduce((n, e) => n + (e.sheets_expected != null ? expectedSheets(e) : 0), 0),
    needsReview: hasReviewField ? exams.reduce((n, e) => n + (e.sheets_needs_review ?? 0), 0) : null,
    examsNeedingReview: exams.filter((e) => (e.sheets_needs_review ?? 0) > 0).length,
    avgScore,
    markedExams: marked.length,
    avgDelta,
  };
}


/** Header subtitle: "8 exams · 467 of 739 answer sheets scanned · 16 sheets waiting for your review." */
export function listSubtitle(s: ListStats): string {
  const scanned = s.expected > 0
    ? `${s.uploaded} of ${s.expected} answer ${s.expected === 1 ? 'sheet' : 'sheets'} scanned`
    : `${plural(s.uploaded, 'answer sheet')} scanned`;
  const parts = [plural(s.exams, 'exam'), scanned];
  if (s.needsReview != null) {
    parts.push(s.needsReview > 0 ? `${plural(s.needsReview, 'sheet')} waiting for your review` : 'nothing waiting for your review');
  }
  return `${parts.join(' · ')}.`;
}
